import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { BackPill } from './BackPill';
import { ChevronLeft, Plus, Trash2, Image as ImageIcon, Volume2, VolumeX } from 'lucide-react';
import { useHistoryClose } from '../../hooks/useHistoryClose';
import { pingAuraFuse } from '../../utils/auraPing';
import { useAuth } from '../../contexts/AuthContext';
import * as VisionMusic from '../../utils/visionMusic';

/* ═══════════════════════════════════════════════════════════════════
   THE VISION — cinematic vision-board slideshow
   • Header:  "{Name}'s Vision"  +  Forge button (top-right)
   • Big rectangle (≈ 85 % h):   image slideshow, one image at a time,
                                  auto-advances every 5 s (fade cross-
                                  transition), tap to pause / resume.
   • Small rectangle (≈ 15 % h): text belt with user's avatar on the
                                  left + vision text on the right.  If
                                  the user leaves text blank while
                                  forging, we auto-attach one of the
                                  built-in positive affirmations.
   • 0-cost, 0-cloud: images stored as compressed data-URLs in
     localStorage (`vision_slides_v1`).
   ═══════════════════════════════════════════════════════════════════ */

const GOLD      = '#C5A059';
const GOLD_HI   = '#E9C77A';
const STORE_KEY = 'vision_slides_v1';
const SLIDE_MS  = 5000;

// Positive affirmations used when the user forges an image without text.
const AFFIRMATIONS = [
  'I attract abundance effortlessly.',
  'My vision becomes reality.',
  'I am unstoppable in pursuit of my dreams.',
  'Wealth flows to me with grace.',
  'Every step forward is sacred.',
  'I hold the power to create my future.',
  'Discipline is my crown, clarity my compass.',
  'The universe conspires in my favour.',
  'I rise, I refine, I reign.',
  'Bliss is my birthright.',
  'I move with quiet, sovereign strength.',
  'My mind is calm, my aim is true.',
  'I am the architect of my destiny.',
  'Success finds me where I stand.',
  'My presence is my power.',
  'I honour the vision only I can see.',
  'What I focus on, I magnify.',
  'I am aligned with my highest self.',
  'Fortune favours my forward motion.',
  'I breathe purpose into every hour.',
  'My legacy is being written today.',
  'I am chosen, and I choose myself first.',
  'Every dawn crowns me anew.',
  'I turn intention into inevitability.',
  'The path bends toward my will.',
];

const pickAffirmation = () =>
  AFFIRMATIONS[Math.floor(Math.random() * AFFIRMATIONS.length)];

const loadSlides = () => {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch { /* noop */ }
  return [];
};

const saveSlides = (arr) => {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(arr)); } catch { /* noop */ }
};

// Compress the incoming file to max 1200 px on the longer edge (JPEG,
// quality 0.82) so localStorage stays under the ~5 MB browser cap.
const compressToDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = () => reject(new Error('read-fail'));
  reader.onload = () => {
    const img = new Image();
    img.onerror = () => reject(new Error('decode-fail'));
    img.onload = () => {
      const MAX = 1200;
      let { width: w, height: h } = img;
      if (w > MAX || h > MAX) {
        const r = Math.min(MAX / w, MAX / h);
        w = Math.round(w * r); h = Math.round(h * r);
      }
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL('image/jpeg', 0.82));
    };
    img.src = reader.result;
  };
  reader.readAsDataURL(file);
});

/* ────────────────────────── COMPONENT ────────────────────────── */
export const TheVision = ({ onClose }) => {
  const { user } = useAuth() || {};
  const displayName = (
    user?.nickname ||
    user?.name ||
    (user?.email ? user.email.split('@')[0] : 'Sovereign')
  );
  const avatar = user?.profile_picture || user?.avatar_url || null;

  const [slides, setSlides] = useState(loadSlides);
  const [idx, setIdx] = useState(0);
  const [visionOpen, setVisionOpen] = useState(false);      // right-side "Vision" panel (Forge / Board)
  const [visionTab, setVisionTab] = useState('forge');       // 'forge' | 'board'

  // Back walks: Vision panel → close tile.
  useHistoryClose(true, useCallback(() => {
    if (visionOpen) {
      setVisionOpen(false);
      try { window.history.pushState({ __modal: Date.now() }, ''); } catch (_) { /* noop */ }
    } else {
      onClose();
    }
  }, [visionOpen, onClose]));

  // Persist whenever slides change.
  useEffect(() => { saveSlides(slides); }, [slides]);
  // Guard idx range whenever slides shrink.
  useEffect(() => {
    if (idx >= slides.length) setIdx(0);
  }, [slides, idx]);

  // Auto-advance slideshow (never paused — controls removed per spec).
  useEffect(() => {
    if (slides.length < 2) return undefined;
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % slides.length);
    }, SLIDE_MS);
    return () => clearInterval(t);
  }, [slides.length]);

  // Continuous bliss music — starts on the FIRST user gesture on Vision.
  // We create the AudioContext INSIDE the gesture (via forceRestart) so
  // Android/Chrome WebView doesn't leave it stuck in "suspended" state.
  const [musicMuted, setMusicMuted] = useState(() => VisionMusic.isMuted());
  useEffect(() => {
    if (slides.length === 0 || musicMuted) { VisionMusic.stop(); return undefined; }
    // Try once eagerly — works if AudioContext is already unlocked from a
    // previous tap elsewhere in the app.
    VisionMusic.start();
    // Fallback: first tap inside Vision force-restarts with a fresh gesture-scoped context.
    const tryStart = () => {
      if (VisionMusic.isPlaying()) {
        cleanup();
        return;
      }
      VisionMusic.forceRestart();
      if (VisionMusic.isPlaying()) cleanup();
    };
    const cleanup = () => {
      window.removeEventListener('touchstart',  tryStart);
      window.removeEventListener('pointerdown', tryStart);
      window.removeEventListener('click',       tryStart);
    };
    window.addEventListener('touchstart',  tryStart, { passive: true });
    window.addEventListener('pointerdown', tryStart, { passive: true });
    window.addEventListener('click',       tryStart);
    return cleanup;
  }, [slides.length, musicMuted]);
  // Always stop the loop when Vision unmounts (Back / close).
  useEffect(() => () => VisionMusic.stop(), []);

  const toggleMusic = () => {
    const nowMuted = !musicMuted;
    setMusicMuted(nowMuted);
    VisionMusic.setMuted(nowMuted);
    // Guaranteed start inside this click handler when un-muting.
    if (!nowMuted) VisionMusic.forceRestart();
  };

  const current = slides[idx] || null;

  const removeSlide = (id) => {
    setSlides((prev) => prev.filter((s) => s.id !== id));
  };

  return (
    <div
      className="fixed inset-0 z-[240] flex flex-col overflow-hidden"
      style={{
        background: 'radial-gradient(ellipse at 50% 0%, #1A1310 0%, #0A0705 65%)',
        paddingTop: 'max(var(--safe-area-inset-top, env(safe-area-inset-top, 0px)), 12px)',
        paddingBottom: 'calc(max(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)), 28px) + 12px)',
      }}
      data-testid="the-vision-modal"
    >
      {/* ────────── Header ────────── */}
      <div className="flex-shrink-0 flex items-center justify-between px-3 py-2">
        <div className="flex items-center gap-2">
          <BackPill onClick={onClose} accent="#E9C77A" testId="vision-back-btn"/>
        </div>

        <div className="text-center">
          <div
            className="text-[10px] uppercase tracking-[0.35em]"
            style={{ color: GOLD_HI }}
          >
            Vision
          </div>
          <div
            className="text-lg font-['Cormorant_Garamond'] leading-tight"
            style={{ color: '#F5EBD8' }}
            data-testid="vision-owner-title"
          >
            {displayName}&rsquo;s Vision
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Bliss-music toggle — right side, next to Forge Vision.  Tapping
              this force-restarts the audio graph inside a fresh gesture so
              Android WebView can never leave the context stuck in "suspended". */}
          {slides.length > 0 && (
            <button
              onClick={toggleMusic}
              className="w-9 h-9 rounded-full flex items-center justify-center"
              style={{
                border: `1px solid ${GOLD}`,
                background: musicMuted ? 'rgba(255,255,255,0.04)' : 'rgba(197,160,89,0.20)',
                color: musicMuted ? 'rgba(255,255,255,0.55)' : GOLD_HI,
                boxShadow: musicMuted ? 'none' : `0 0 12px ${GOLD}66`,
              }}
              aria-label={musicMuted ? 'Play bliss music' : 'Mute bliss music'}
              data-testid="vision-music-toggle"
            >
              {musicMuted ? <VolumeX className="w-4 h-4"/> : <Volume2 className="w-4 h-4"/>}
            </button>
          )}
          <button
            onClick={() => { setVisionTab('forge'); setVisionOpen(true); }}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] uppercase tracking-[0.25em] font-['Cormorant_Garamond']"
            style={{
              background: 'linear-gradient(180deg, rgba(197,160,89,0.20), rgba(197,160,89,0.08))',
              border: `1px solid ${GOLD}`,
              color: GOLD_HI,
            }}
            data-testid="vision-open-panel-btn"
          >
            <Plus className="w-3.5 h-3.5" />
            Vision
          </button>
        </div>
      </div>

      {/* ────────── Big Vision window (~85%) ────────── */}
      <div
        className="flex-1 min-h-0 mx-3 rounded-2xl relative overflow-hidden"
        style={{
          border: `2px solid ${GOLD_HI}`,
          boxShadow: `
            0 0 0 1px rgba(255,220,150,0.35),
            0 0 24px rgba(233,199,122,0.55),
            0 0 48px rgba(197,160,89,0.35),
            inset 0 0 32px rgba(0,0,0,0.55)
          `,
          background: '#000',
        }}
        onClick={undefined}
        data-testid="vision-slideshow"
      >
        {current ? (
          <>
            {/* Image, fade transition */}
            <img
              key={current.id}
              src={current.image}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              style={{ animation: 'visionFadeIn 800ms ease' }}
              draggable={false}
            />
            {/* Vignette + subtle gold rim */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: 'radial-gradient(ellipse at 50% 100%, rgba(0,0,0,0.55) 0%, transparent 65%)',
              }}
            />
          </>
        ) : (
          <EmptyState onForge={() => { setVisionTab('forge'); setVisionOpen(true); }} />
        )}
      </div>

      {/* ────────── Small text belt (fixed height so it's ALWAYS
             fully visible above the system nav — was `15vh` which got
             squeezed on short Android viewports). ────────── */}
      <div
        className="flex-shrink-0 mx-3 mt-2 mb-1 rounded-xl px-2 py-2 flex items-center gap-3"
        style={{
          background: 'linear-gradient(180deg, rgba(20,14,10,0.92), rgba(10,7,5,0.92))',
          border: `2px solid ${GOLD_HI}`,
          boxShadow: `
            0 0 0 1px rgba(255,220,150,0.32),
            0 0 18px rgba(233,199,122,0.5),
            0 0 34px rgba(197,160,89,0.3)
          `,
          height: 82,
        }}
        data-testid="vision-text-belt"
      >
        {/* Avatar (left) */}
        <div
          className="flex-shrink-0 rounded-full overflow-hidden"
          style={{
            width: 54,
            height: 54,
            border: `1.5px solid ${GOLD}`,
            background: '#1A1310',
          }}
        >
          {avatar ? (
            <img
              src={avatar}
              alt=""
              className="w-full h-full object-cover"
              draggable={false}
            />
          ) : (
            <div
              className="w-full h-full flex items-center justify-center text-lg"
              style={{ color: GOLD_HI, fontFamily: "'Cormorant Garamond', serif" }}
            >
              {displayName.slice(0, 1).toUpperCase()}
            </div>
          )}
        </div>

        {/* Text (right) */}
        <div className="flex-1 min-w-0">
          <div
            className="text-[10px] uppercase tracking-[0.28em] mb-0.5"
            style={{ color: `${GOLD_HI}CC` }}
          >
            {current ? new Date(current.addedAt || Date.now()).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : 'Empty — forge your first vision'}
          </div>
          <div
            className="text-sm md:text-base leading-snug font-['Cormorant_Garamond'] italic line-clamp-2"
            style={{ color: '#F5EBD8' }}
            data-testid="vision-current-text"
          >
            {current ? current.text : 'A vision unforged is a life unlived.'}
          </div>
        </div>
      </div>

      {/* ────────── Vision panel (Forge / Board tabs) ────────── */}
      {visionOpen && (
        <VisionPanel
          tab={visionTab}
          setTab={setVisionTab}
          slides={slides}
          onRemove={removeSlide}
          onClose={() => setVisionOpen(false)}
          onCommit={(newOnes) => {
            setSlides((prev) => [...prev, ...newOnes]);
            setIdx(slides.length);
            setVisionOpen(false);
            try { pingAuraFuse('vision'); } catch (_) { /* noop */ }
          }}
        />
      )}

      {/* Fade-in animation keyframes (scoped inline). */}
      <style>{`
        @keyframes visionFadeIn {
          from { opacity: 0; transform: scale(1.02); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

/* ═════════════════════════ Empty state ═════════════════════════ */
const EmptyState = ({ onForge }) => (
  <div className="w-full h-full flex flex-col items-center justify-center gap-3 px-6 text-center">
    <ImageIcon className="w-10 h-10" style={{ color: `${GOLD_HI}88` }} />
    <div
      className="text-lg font-['Cormorant_Garamond']"
      style={{ color: '#F5EBD8' }}
    >
      Your Vision awaits its first image.
    </div>
    <div className="text-xs text-white/50 max-w-xs leading-relaxed">
      Tap <span style={{ color: GOLD_HI }}>Forge</span> to add a photo and — optionally — the words that anchor it.
    </div>
    <button
      onClick={onForge}
      className="mt-3 px-5 py-2 rounded-full text-xs uppercase tracking-[0.3em]"
      style={{
        background: `linear-gradient(180deg, ${GOLD}, ${GOLD}CC)`,
        color: '#1A1310',
        fontFamily: "'Cormorant Garamond', serif",
        letterSpacing: '0.3em',
      }}
      data-testid="vision-empty-forge-btn"
    >
      Forge First Vision
    </button>
  </div>
);

/* ═════════════════════════ Vision panel (Forge / Board) ═════════════════════════ */
const VisionPanel = ({ tab, setTab, slides, onRemove, onClose, onCommit }) => {
  return (
    <div
      className="absolute inset-0 z-[10] flex flex-col"
      style={{
        background: 'rgba(6,4,3,0.96)',
        paddingTop: 'max(var(--safe-area-inset-top, env(safe-area-inset-top, 0px)), 12px)',
        paddingBottom: 'calc(max(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)), 28px) + 16px)',
      }}
      data-testid="vision-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2">
        <BackPill onClick={onClose} accent="#E9C77A" testId="vision-panel-close"/>
        <div
          className="text-xs uppercase tracking-[0.35em]"
          style={{ color: GOLD_HI }}
        >
          Vision
        </div>
        <div style={{ width: 60 }} />
      </div>

      {/* Sub-tabs — Forge / Board */}
      <div className="flex-shrink-0 px-4 mt-1">
        <div
          className="flex rounded-full p-1"
          style={{
            background: 'rgba(20,14,10,0.6)',
            border: `1px solid ${GOLD}44`,
          }}
          data-testid="vision-panel-tabs"
        >
          {['forge', 'board'].map((t) => {
            const active = t === tab;
            return (
              <button
                key={t}
                onClick={() => setTab(t)}
                className="flex-1 py-2 rounded-full text-[11px] uppercase tracking-[0.35em] font-['Cormorant_Garamond'] transition-all"
                style={{
                  background: active
                    ? `linear-gradient(180deg, ${GOLD_HI}, ${GOLD})`
                    : 'transparent',
                  color: active ? '#1A1310' : `${GOLD_HI}AA`,
                  boxShadow: active
                    ? `0 0 16px rgba(233,199,122,0.5)`
                    : 'none',
                }}
                data-testid={`vision-tab-${t}`}
              >
                {t === 'forge' ? 'Forge' : `Board${slides.length ? ` · ${slides.length}` : ''}`}
              </button>
            );
          })}
        </div>
      </div>

      {/* Body — tab-scoped */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-4 mt-3">
        {tab === 'forge' ? (
          <ForgePane onCommit={onCommit} />
        ) : (
          <BoardPane slides={slides} onRemove={onRemove} />
        )}
      </div>
    </div>
  );
};

/* ═════════════════════════ Forge pane ═════════════════════════ */
const ForgePane = ({ onCommit }) => {
  const [previews, setPreviews] = useState([]); // dataURL[]
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const addFiles = async (fileList) => {
    const chosen = Array.from(fileList).filter((f) => /^image\//.test(f.type));
    if (chosen.length === 0) return;
    setBusy(true);
    const compressed = [];
    for (const f of chosen) {
      try {
        const url = await compressToDataUrl(f);
        compressed.push(url);
      } catch (_) { /* skip broken file */ }
    }
    setPreviews((prev) => [...prev, ...compressed]);
    setBusy(false);
  };

  const removePreview = (i) => {
    setPreviews((prev) => prev.filter((_, k) => k !== i));
  };

  const commit = () => {
    if (previews.length === 0) return;
    const now = Date.now();
    const cleanText = text.trim();
    const newOnes = previews.map((image, i) => ({
      id: `v_${now}_${i}_${Math.random().toString(36).slice(2, 6)}`,
      image,
      text: cleanText || pickAffirmation(),
      addedAt: now + i,
    }));
    onCommit(newOnes);
  };

  return (
    <>
      {/* Picker */}
      <button
        onClick={() => fileRef.current?.click()}
        disabled={busy}
        className="w-full rounded-2xl p-6 flex flex-col items-center justify-center gap-2 mt-1"
        style={{
          border: `1.5px dashed ${GOLD}66`,
          background: 'rgba(20,14,10,0.6)',
          color: GOLD_HI,
          minHeight: 130,
        }}
        data-testid="forge-picker-btn"
      >
        <ImageIcon className="w-8 h-8" />
        <div className="text-sm font-['Cormorant_Garamond'] tracking-[0.15em]">
          {busy ? 'Processing…' : previews.length === 0 ? 'Choose image(s)' : 'Add more'}
        </div>
        <div className="text-[10px] text-white/50 uppercase tracking-[0.25em]">
          One or many · JPG · PNG · WebP
        </div>
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => e.target.files && addFiles(e.target.files)}
        data-testid="forge-file-input"
      />

      {/* Preview grid */}
      {previews.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mt-3" data-testid="forge-preview-grid">
          {previews.map((p, i) => (
            <div
              key={i}
              className="relative rounded-lg overflow-hidden"
              style={{ aspectRatio: '1 / 1', border: `1px solid ${GOLD}55` }}
            >
              <img src={p} alt="" className="w-full h-full object-cover" />
              <button
                onClick={() => removePreview(i)}
                className="absolute top-1 right-1 p-1 rounded-full"
                style={{ background: 'rgba(0,0,0,0.6)' }}
                aria-label="Remove"
              >
                <Trash2 className="w-3 h-3 text-white/80" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Text input */}
      <div className="mt-4">
        <div
          className="text-[10px] uppercase tracking-[0.3em] mb-2"
          style={{ color: `${GOLD_HI}CC` }}
        >
          Anchor Text  <span className="opacity-60 normal-case tracking-normal">(optional — one of 25 positive lines will be gifted if left blank)</span>
        </div>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, 180))}
          placeholder="e.g. Sovereign of every dawn."
          rows={3}
          className="w-full rounded-xl px-3 py-2 text-sm resize-none"
          style={{
            background: 'rgba(0,0,0,0.5)',
            border: `1px solid ${GOLD}44`,
            color: '#F5EBD8',
            fontFamily: "'Cormorant Garamond', serif",
            lineHeight: 1.5,
          }}
          data-testid="forge-text-input"
        />
        <div className="text-[10px] text-white/40 mt-1 text-right">
          {text.length} / 180
        </div>
      </div>

      {/* Seal Vision — anchored beneath text box */}
      <button
        disabled={previews.length === 0 || busy}
        onClick={commit}
        className="mt-4 w-full py-3 rounded-xl text-sm uppercase tracking-[0.35em] font-['Cormorant_Garamond'] transition-all"
        style={{
          background: previews.length === 0
            ? 'rgba(197,160,89,0.15)'
            : `linear-gradient(180deg, ${GOLD_HI}, ${GOLD})`,
          color: previews.length === 0 ? '#5F544A' : '#1A1310',
          border: `1.5px solid ${GOLD_HI}`,
          boxShadow: previews.length === 0
            ? 'none'
            : `0 0 22px rgba(233,199,122,0.55), 0 0 44px rgba(197,160,89,0.35)`,
          opacity: previews.length === 0 ? 0.55 : 1,
          cursor: previews.length === 0 ? 'not-allowed' : 'pointer',
        }}
        data-testid="forge-commit-btn"
      >
        {busy ? 'Processing…' : 'Seal Vision'}
      </button>
    </>
  );
};

/* ═════════════════════════ Board pane ═════════════════════════ */
const BoardPane = ({ slides, onRemove }) => {
  if (slides.length === 0) {
    return (
      <div
        className="w-full text-center py-16 rounded-2xl"
        style={{
          border: `1.5px dashed ${GOLD}44`,
          background: 'rgba(20,14,10,0.4)',
        }}
        data-testid="board-empty"
      >
        <ImageIcon className="w-8 h-8 mx-auto mb-2" style={{ color: `${GOLD_HI}88` }} />
        <div
          className="text-sm font-['Cormorant_Garamond']"
          style={{ color: '#F5EBD8' }}
        >
          No visions yet — switch to Forge to add your first.
        </div>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="board-grid">
      {slides.map((s) => (
        <div
          key={s.id}
          className="relative rounded-xl overflow-hidden"
          style={{
            aspectRatio: '1 / 1',
            border: `1.5px solid ${GOLD_HI}88`,
            boxShadow: `0 0 10px rgba(233,199,122,0.28)`,
          }}
          data-testid={`board-slide-${s.id}`}
        >
          <img
            src={s.image}
            alt=""
            className="w-full h-full object-cover"
            draggable={false}
          />
          {/* Caption overlay */}
          <div
            className="absolute inset-x-0 bottom-0 px-2 py-1.5"
            style={{
              background: 'linear-gradient(180deg, transparent, rgba(0,0,0,0.85))',
              color: '#F5EBD8',
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 11,
              lineHeight: 1.25,
            }}
          >
            <div className="line-clamp-2 italic">{s.text}</div>
          </div>
          {/* Delete */}
          <button
            onClick={() => onRemove(s.id)}
            className="absolute top-1.5 right-1.5 p-1.5 rounded-full"
            style={{
              background: 'rgba(0,0,0,0.6)',
              border: '1px solid rgba(255,255,255,0.18)',
              color: '#E7C6C6',
            }}
            aria-label="Remove vision"
            data-testid={`board-delete-${s.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
};

export default TheVision;

/* ═════════════════════ Launcher tile icon ═════════════════════ */
/* Small SVG rendered in the TryLauncher grid — a gold eye/vision
   glyph.  Extracted here so importing components don't need to know
   the internals of TheVision. */
export const VisionTileIcon = ({ size = 30, accent = '#E9C77A' }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" aria-hidden="true">
    <ellipse cx="16" cy="16" rx="12" ry="7" stroke={accent} strokeWidth="1.4" opacity="0.9" />
    <circle cx="16" cy="16" r="4" stroke={accent} strokeWidth="1.4" />
    <circle cx="16" cy="16" r="1.7" fill={accent} />
    <path d="M4 16 Q16 6 28 16" stroke={accent} strokeWidth="1" opacity="0.4" fill="none" />
    <path d="M4 16 Q16 26 28 16" stroke={accent} strokeWidth="1" opacity="0.4" fill="none" />
  </svg>
);
